import React from 'react';
import axios from '../../helpers/AxiosInstance';

// Frontend
import { Grid, Cell } from 'react-foundation';
import DemoStartButton from '../../components/DemoStartButton';
import '../../stylesheets/components/forms/Conversations.css';
import '../../stylesheets/components/data-tables/Conversations.css';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';

// Functionality
import { Link, useLocation } from 'react-router-dom';

// Components
import {
  Header,
  DluchApi,
  FlashMessage,
  ProgressBar,
  ErrorRender,
  setToken,
} from '../../constants/SharedComponents';

import useCurrentUser from '../../hooks/useCurrentUser';
import DataTable from 'react-data-table-component';
import { FilterComponent, dataTableColumns } from '../../helpers/Conversation';
import useWindowDimensions from '../../hooks/useWindowDimensions';

export function ConversationsIndex() {
  // API
  const currentUser = useCurrentUser();
  const { state } = useLocation();
  const token = setToken(currentUser);
  const headerParams = { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 };

  // Objects
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const { width } = useWindowDimensions();

  // Functions
  function retryFetch() {
    setLoading(true);
    setError(false);
    setData(null);
    getData();
  }

  const getData = async () => {
    try {
      let Api = new DluchApi('ConversationsIndex');
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.conversations;
      setData(responseData);
      if (responseData && responseData.length < 1) {
        setEmptyData(true);
      }
      setError(false);
    } catch (err) {
      setError(err.message);
      setEmptyData(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // @see https://css-tricks.com/run-useeffect-only-once/#
  // The second param will ensure the useEffect only runs once.
  // eslint-disable-next-line
  React.useEffect(() => {
    if (currentUser?.token) {
      retryFetch();
    }

    if (state?.hasOwnProperty('flash')) {
      setFlashMessageObject(state.flash);
    }
  }, [currentUser, state]);

  // DataTable
  const [filterText, setFilterText] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [emptyData, setEmptyData] = React.useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(false);
  let filteredItems;

  if (data && data.length > 0) {
    filteredItems = data.filter((item) => {
      return (
        ((item.organiserName?.toLowerCase().includes(filterText.toLowerCase())) ||
          (item.attendeeName?.toLowerCase().includes(filterText.toLowerCase()))) &&
        (filterStatus ? item.status === parseInt(filterStatus) : true)
      );
    });
  }

  const filterTextFunction = React.useCallback((event) => {
    setFilterText(event.target.value);
  }, []);

  const filterStatusFunction = React.useCallback((event) => {
    setFilterStatus(event.target.value);
  }, []);

  const handleClear = React.useCallback(() => {
    setResetPaginationToggle(!resetPaginationToggle);
    setFilterText('');
    setFilterStatus('');
  }, [resetPaginationToggle]);

  return (
    <>
      <ProgressBar loading={loading} />
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      {error && (
        <ErrorRender object={error} />
      )}

      <div className="page-content">
        <div className="page-content__header">
          <div className="grid-container">
            <Grid className="grid-padding-x align-justify align-middle">
              <Cell small={12} large={4}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingBottom: '15px', maxWidth: '370px' }}>
                  <Link
                    to="/conversations/new"
                    id="page-navigation-button--create"
                    className="page-content__button"
                  >
                    <IoChatbubbleEllipsesOutline size={20} style={{ marginRight: '8px' }} />
                    Create Conversation Request
                  </Link>
                </div>
              </Cell>
              <Cell small={12} large={8}>
                {width >= 1024 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', paddingBottom: '8px' }}>
                    <div style={{ width: '100%', maxWidth: '600px' }}>
                      <FilterComponent
                        onFilterText={filterTextFunction}
                        onFilterStatus={filterStatusFunction}
                        onClear={handleClear}
                        filterText={filterText}
                        filterStatus={filterStatus}
                      />
                    </div>
                  </div>
                )}
                {width < 1024 && (
                  <div style={{ paddingBottom: '8px' }}>
                    <div style={{ width: '100%', maxWidth: '600px' }}>
                      <FilterComponent
                        onFilterText={filterTextFunction}
                        onFilterStatus={filterStatusFunction}
                        onClear={handleClear}
                        filterText={filterText}
                        filterStatus={filterStatus}
                      />
                    </div>
                  </div>
                )}
              </Cell>
            </Grid>
          </div>
        </div>
      </div>
      {currentUser && emptyData && (
        <div className="grid-container">
          <Grid className="grid-padding-x align-justify">
            <Cell small={12}>
              <div className="page-content">
                <h1 className="page-content__title">
                  Welcome {currentUser.firstname} to your dashboard,
                </h1>

                <div className="page-content__styles">
                  <p>
                    <strong>
                      You currently don't have any conversations. Conversations will appear here
                      as they are created.
                    </strong>
                  </p>
                  <p>
                    We recommend that you explore:&nbsp;
                    <Link to="/pages/how-to-use-this-tool" className="page-content__button--medium">
                      How to use this tool
                    </Link>
                  </p>
                  <p>
                    <strong>Or try our interactive demo:</strong>
                  </p>
                  <div style={{ marginTop: '20px' }}>
                    <DemoStartButton variant="secondary" />
                  </div>
                </div>
              </div>
            </Cell>
          </Grid>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid-container">
          <Grid className="grid-padding-x align-justify">
            <Cell large={12}>
              <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                * Person who created the conversation
              </div>
            </Cell>
            <Cell large={12} className="js--data-table-wrap">
              <DataTable
                columns={dataTableColumns}
                data={filteredItems}
                defaultSortFieldId={3}
                responsive
                fixedHeader
                pagination
                defaultSortAsc={false}
              />
            </Cell>
          </Grid>
        </div>
      )}
    </>
  );
}
